import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ImageBox } from '../../../components/BoxedImage';
import { ImageItemDetailsDialog } from '../../../components/ImageItemDetailsDialog';
import { ImageViewer } from '../../../components/ImageViewer';
import { StoreDateTime } from '../../../components/StoreDateTime';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { FORGE_REMOTE_DC_TOOL_PATH } from '../../../forge/paths';
import { ApiService } from '../../../services/api';
import {
  BBox,
  CameraDetails,
  ControlPogResetMappingCandidate,
  ControlPogResetMappingClaim,
  ControlPogResetMappingMod,
  ControlPogResetPlanogramProduct,
  ProductRecognitionPolygon,
  ProductRecognitionResponse,
} from '../../../types';
import { isShelfTagCode } from '../../../utils/modTag';
import { ImageZoomModal } from '../../embeddings/components/ImageZoomModal';
import { PlanogramProductDialog } from './PlanogramProductDialog';
import { PogResetFlagBadges } from './PogResetFlagBadge';

// Matched / not-matched coloring shared by the planogram boxes and the camera polygons:
// green = the detection's product is in this planogram mod, red = it isn't.
// blue = the product IS in this mod but the match similarity is below the gate,
// so the matcher does not count it (explains red planogram boxes on 100%-score mods).
const MATCHED_COLOR = '#22c55e';
const UNMATCHED_COLOR = '#ef4444';
const WEAK_MATCH_COLOR = '#3b82f6';
// Cross-highlight (hovered identity on either side).
const HIGHLIGHT_COLOR = '#eab308';

type HoverIdentity = { items: Set<string>; codes: Set<string> };

const polygonIdentity = (polygon: ProductRecognitionPolygon): HoverIdentity => {
  const best = polygon.vizSimCandidates?.[0];
  return {
    items: new Set(best?.itemNumber ? [best.itemNumber] : []),
    codes: new Set(
      best ? [best.normalizedBarcode, ...(best.allNormalizedBarcodes ?? [])].filter(Boolean) : []
    ),
  };
};

const identityMatches = (
  identity: HoverIdentity | null,
  itemNumber: string | null | undefined,
  codes: (string | null | undefined)[]
): boolean => {
  if (!identity) return false;
  if (itemNumber && identity.items.has(itemNumber)) return true;
  return codes.some((code) => code && identity.codes.has(code));
};

const formatScore = (score: number): string => `${Math.round(score * 100)}%`;

// "Claimed" = this physical mod is the PROPOSED mapping of another open reset task
// (distinct from "occupied" = currently assigned to another active POG).
// Links to the claiming task's detail page in a new tab.
const ClaimedChip: React.FC<{ claim: ControlPogResetMappingClaim; storeName: string }> = ({
  claim,
  storeName,
}) => (
  <Link
    to={`/stores/${storeName}/pog-reset/${claim.taskId}`}
    target="_blank"
    rel="noreferrer"
    onClick={(e) => e.stopPropagation()}
    className="ml-2 text-xs font-sans font-medium text-purple-700 hover:underline whitespace-nowrap"
    title={
      `Proposed to another open reset task: ${claim.pogName ?? 'unknown POG'}` +
      `${claim.pogNumber ? ` (${claim.pogNumber})` : ''}, mod ${claim.modNumber ?? '?'}` +
      `${claim.score != null ? ` — score ${formatScore(claim.score)}` : ''}. Click to open that task.`
    }
  >
    ⚠ claimed
  </Link>
);

const useLoadedImage = (src: string | null): HTMLImageElement | null => {
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    setLoadedImage(null);
    if (!src) return undefined;
    let active = true;
    const image = new Image();
    image.onload = () => {
      if (active) setLoadedImage(image);
    };
    image.src = src;
    return () => {
      active = false;
    };
  }, [src]);
  return loadedImage;
};

export type ModDecision = {
  source: 'proposal' | 'candidate' | 'manual' | 'scan' | 'missing';
  tagName: string | null;
  modCoverageId: number | null;
};

interface PogResetModCardProps {
  displayNumber: number | string;
  readOnly: boolean;
  mod: ControlPogResetMappingMod;
  matchThreshold: number;
  minCandidateSimilarity: number;
  showEmbeddingsCount: boolean;
  storeName: string;
  storeTimeZone: string | undefined;
  // Mapping decision is owned by the page so all mods can be submitted together.
  decision: ModDecision | null;
  onDecisionChange: (decision: ModDecision | null) => void;
}

export const PogResetModCard: React.FC<PogResetModCardProps> = ({
  mod,
  displayNumber,
  readOnly,
  matchThreshold,
  minCandidateSimilarity,
  showEmbeddingsCount,
  storeName,
  storeTimeZone,
  decision,
  onDecisionChange,
}) => {
  const { token } = useAuthContext();
  const { showErrorToast } = useNotificationContext();
  const [selectedProductIndex, setSelectedProductIndex] = useState<number | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<number | null>(null);
  const [zoomImageSrc, setZoomImageSrc] = useState<string | null>(null);
  const [prByImageId, setPrByImageId] = useState<
    Record<number, ProductRecognitionResponse | 'error'>
  >({});
  const [selectedDetectionItem, setSelectedDetectionItem] =
    useState<ProductRecognitionPolygon | null>(null);
  // Overlay layer toggles: products on by default (they drive the matching), cartons off.
  const [showProductPolygons, setShowProductPolygons] = useState(true);
  const [showCartonPolygons, setShowCartonPolygons] = useState(false);
  // Identity hovered on either image; matching shapes on both sides highlight.
  const [hoveredIdentity, setHoveredIdentity] = useState<HoverIdentity | null>(null);
  const [manualTag, setManualTag] = useState('');
  const [selectedDetectionColor, setSelectedDetectionColor] = useState<string | undefined>(
    undefined
  );

  // Cameras (cached) give, per camera, the mod list ordered to align with the PR
  // polygon `.mod` index — so `camera.mods[polygon.mod]` is that polygon's mod. Used
  // to hide detections belonging to a *different* mod on the same (multi-mod) image.
  const [cameras, setCameras] = useState<CameraDetails[]>([]);
  useEffect(() => {
    if (!storeName) return undefined;
    let active = true;
    ApiService.fetchCameras(token, storeName)
      .then((data) => {
        if (active) setCameras(data);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [token, storeName]);

  const planogramImage = useLoadedImage(mod.planogramImageUri);

  // Pan-vs-click guard: BoxedImage fires box onClick even after a zoom-pan drag,
  // so ignore clicks when the pointer moved more than a few pixels since mousedown.
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const wasDraggedRef = useRef(false);
  const dragGuardProps = {
    onMouseDownCapture: (e: React.MouseEvent) => {
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      wasDraggedRef.current = false;
    },
    onMouseMoveCapture: (e: React.MouseEvent) => {
      const start = dragStartRef.current;
      if (start && (Math.abs(e.clientX - start.x) > 5 || Math.abs(e.clientY - start.y) > 5)) {
        wasDraggedRef.current = true;
      }
    },
  };

  // Scan options in the PR-candidate shape so selection/preview treats them uniformly:
  // score = install-scan confidence; the image fields are the scanned tag's own PR
  // evidence (when its camera produced any), so the preview always matches the row.
  const scanAsCandidates: ControlPogResetMappingCandidate[] = useMemo(
    () =>
      mod.scanCandidates
        .filter((scan) => !mod.candidates.some((c) => c.modCoverageId === scan.modCoverageId))
        .map((scan) => ({
          rank: 0,
          modCoverageId: scan.modCoverageId,
          tagName: scan.tagName,
          cameraId: scan.cameraId,
          deviceName: scan.deviceName,
          score: scan.score,
          precision: null,
          coverage: null,
          matchedCount: 0,
          realogramItemCount: null,
          inferenceId: scan.inferenceId,
          imageId: scan.imageId,
          imageUri: scan.imageUri,
          inferenceCreatedAt: scan.inferenceCreatedAt,
          prMod: scan.prMod,
          obstructionRatio: null,
          isTrackingExcluded: false,
          assignedPogName: null,
          assignedPogNumber: null,
          claimedBy: null,
        })),
    [mod.scanCandidates, mod.candidates]
  );

  // Camera panel shows the proposed candidate by default, else the top-ranked one.
  const selectedCandidate: ControlPogResetMappingCandidate | null = useMemo(() => {
    const allRows = [...scanAsCandidates, ...mod.candidates];
    if (selectedCandidateId != null) {
      return allRows.find((c) => c.modCoverageId === selectedCandidateId) ?? null;
    }
    if (mod.proposed) {
      const proposed = allRows.find((c) => c.modCoverageId === mod.proposed?.modCoverageId);
      if (proposed) return proposed;
    }
    return allRows[0] ?? null;
  }, [mod, selectedCandidateId, scanAsCandidates]);

  const cameraImage = useLoadedImage(selectedCandidate?.imageUri ?? null);

  // A proposal whose mod is flagged no_match (best score below the match threshold):
  // shown but not auto-accepted, so the reviewer decides consciously.
  const isWeakProposal = Boolean(mod.proposed) && mod.flags.includes('no_match');

  // Shared by the Set button and Enter in the manual-tag input.
  const handleSetManualTag = () => {
    const trimmed = manualTag.trim().toUpperCase();
    if (!trimmed) return;
    if (!isShelfTagCode(trimmed)) {
      showErrorToast(`“${trimmed}” is not a valid mod tag`);
      return;
    }
    onDecisionChange({ source: 'manual', tagName: trimmed, modCoverageId: null });
    setManualTag('');
  };

  // PR `.mod` indexes belonging to the selected candidate's mod. A camera image can
  // span several mods; `camera.mods` is ordered to align with the PR `.mod` index, but
  // `camera.mods` is 0-based while the polygon `.mod` is 1-based — so camera position i
  // corresponds to PR `.mod` = i + 1 (same as the backend's legacy `mod_number + 1`).
  // null = mapping unavailable (cameras not loaded / mod not found) → don't filter.
  const selectedModPrIndexes = useMemo<Set<number> | null>(() => {
    if (selectedCandidate == null) return null;
    // Preferred: the exact PR mod index the backend matched this coverage to (area_id,
    // legacy metadata or install-v2 click points) — the same index the score used.
    if (selectedCandidate.prMod != null) {
      return new Set([selectedCandidate.prMod]);
    }
    // Fallback (older cached docs): positional alignment — the candidate carries the
    // exact camera it was detected on; resolve that one (a modCoverageId can appear on
    // more than one camera's mods).
    const camera = cameras.find((c) => c.cameraId === selectedCandidate.cameraId);
    if (!camera) return null;
    const indexes = new Set<number>();
    camera.mods.forEach((m, index) => {
      // PR `.mod` is 1-based; camera position 0 → PR mod 1.
      if (m.modCoverageId === selectedCandidate.modCoverageId) indexes.add(index + 1);
    });
    return indexes;
  }, [cameras, selectedCandidate]);

  // Fetch the stored inference (detections) for the selected candidate's camera image.
  useEffect(() => {
    const imageId = selectedCandidate?.imageId;
    if (imageId == null || prByImageId[imageId] !== undefined) return;
    let active = true;
    ApiService.runRayProductRecognition(token, imageId, true)
      .then((data) => {
        if (active) setPrByImageId((prev) => ({ ...prev, [imageId]: data }));
      })
      .catch(() => {
        // Inference may have expired (1-week TTL) — fall back to the plain image.
        if (active) setPrByImageId((prev) => ({ ...prev, [imageId]: 'error' }));
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCandidate?.imageId, token]);

  // Per-layer top-candidate identities visible on the selected candidate's camera image
  // (with the same ≥ similarity gate the matching algorithm applies server-side).
  const detectedIdentities = useMemo(() => {
    const imageId = selectedCandidate?.imageId;
    const prData = imageId != null ? prByImageId[imageId] : undefined;
    if (!prData || prData === 'error') return null;
    const collect = (polygons: ProductRecognitionPolygon[]) => {
      const items = new Set<string>();
      const codes = new Set<string>();
      for (const polygon of polygons) {
        // Multi-mod image: keep only detections in the selected candidate's mod
        // (mirrors the camera-detection overlay's filter).
        if (
          selectedModPrIndexes != null &&
          polygon.mod != null &&
          !selectedModPrIndexes.has(polygon.mod)
        ) {
          continue;
        }
        const best = polygon.vizSimCandidates?.[0];
        if (!best) continue;
        if (best.similarity == null || best.similarity < minCandidateSimilarity) continue;
        if (best.itemNumber) items.add(best.itemNumber);
        if (best.normalizedBarcode) codes.add(best.normalizedBarcode);
        best.allNormalizedBarcodes?.forEach((barcode) => codes.add(barcode));
      }
      return { items, codes };
    };
    return {
      products: collect(prData.productPolygons ?? []),
      cartons: collect(prData.cartonPolygons ?? []),
    };
  }, [selectedCandidate?.imageId, prByImageId, minCandidateSimilarity, selectedModPrIndexes]);

  // Planogram coloring follows the enabled overlay layers: products, cartons, or both.
  const activeDetected = useMemo(() => {
    if (!detectedIdentities || (!showProductPolygons && !showCartonPolygons)) return null;
    const items = new Set<string>();
    const codes = new Set<string>();
    if (showProductPolygons) {
      detectedIdentities.products.items.forEach((item) => items.add(item));
      detectedIdentities.products.codes.forEach((code) => codes.add(code));
    }
    if (showCartonPolygons) {
      detectedIdentities.cartons.items.forEach((item) => items.add(item));
      detectedIdentities.cartons.codes.forEach((code) => codes.add(code));
    }
    return { items, codes };
  }, [detectedIdentities, showProductPolygons, showCartonPolygons]);

  // This planogram mod's identity set — used to color each camera detection green
  // (its product belongs to the planogram) or red (it doesn't).
  const planogramIdentity = useMemo<HoverIdentity>(() => {
    const items = new Set<string>();
    const codes = new Set<string>();
    for (const product of mod.products) {
      if (product.itemNumber) items.add(product.itemNumber);
      if (product.upc) codes.add(product.upc);
    }
    return { items, codes };
  }, [mod.products]);

  // Product/carton detection polygons, colored by whether they match this planogram mod.
  const cameraDetections: ImageBox[] = useMemo(() => {
    const imageId = selectedCandidate?.imageId;
    const prData = imageId != null ? prByImageId[imageId] : undefined;
    if (!prData || prData === 'error') return [];

    const polygonToBox = (polygon: ProductRecognitionPolygon): ImageBox | null => {
      if (!polygon.points || polygon.points.length === 0) return null;
      // Multi-mod image: keep only detections in the selected candidate's mod.
      // When the PR-index mapping is unavailable, show everything (no filtering).
      if (
        selectedModPrIndexes != null &&
        polygon.mod != null &&
        !selectedModPrIndexes.has(polygon.mod)
      ) {
        return null;
      }
      const xCoords = polygon.points.map((p) => p.x);
      const yCoords = polygon.points.map((p) => p.y);
      const x = Math.min(...xCoords);
      const y = Math.min(...yCoords);
      // Same ≥ similarity gate the matching algorithm applies server-side: a
      // below-threshold best candidate carries no confident identity.
      const topCandidate = polygon.vizSimCandidates?.[0];
      const meetsGate =
        topCandidate?.similarity != null && topCandidate.similarity >= minCandidateSimilarity;
      const codes = topCandidate
        ? [topCandidate.normalizedBarcode, ...(topCandidate.allNormalizedBarcodes ?? [])]
        : [];
      const isHighlighted = identityMatches(hoveredIdentity, topCandidate?.itemNumber, codes);
      // Green when this detection's product belongs to the planogram mod and passes the
      // gate; blue when it belongs but the similarity is too low to count; else red.
      const inPlanogramMod = identityMatches(planogramIdentity, topCandidate?.itemNumber, codes);
      return {
        shape: [x, y, Math.max(...xCoords) - x, Math.max(...yCoords) - y],
        isPolygon: true,
        points: polygon.points,
        color: isHighlighted
          ? HIGHLIGHT_COLOR
          : inPlanogramMod
            ? meetsGate
              ? MATCHED_COLOR
              : WEAK_MATCH_COLOR
            : UNMATCHED_COLOR,
        // Same width the store-details camera view uses for its detections.
        strokeWidth: isHighlighted ? 16 : 10,
        opacity: isHighlighted ? 0.35 : undefined,
        item: polygon,
      };
    };

    const cartons = showCartonPolygons
      ? ((prData.cartonPolygons ?? []).map(polygonToBox).filter(Boolean) as ImageBox[])
      : [];

    const products = showProductPolygons
      ? ((prData.productPolygons ?? []).map(polygonToBox).filter(Boolean) as ImageBox[])
      : [];

    return [...cartons, ...products];
  }, [
    selectedCandidate?.imageId,
    prByImageId,
    showProductPolygons,
    showCartonPolygons,
    hoveredIdentity,
    planogramIdentity,
    selectedModPrIndexes,
    minCandidateSimilarity,
  ]);

  const productBoxes: BBox[] = useMemo(
    () =>
      mod.products
        .filter((product) => product.bbox && product.bbox.length === 4)
        .map((product) => {
          const [x, y, width, height] = product.bbox as [number, number, number, number];
          // Colored by whichever overlay layers are enabled (products mirror the backend
          // matching scope; enabling cartons widens the visual to carton signal too).
          const detected = activeDetected
            ? Boolean(
                (product.itemNumber && activeDetected.items.has(product.itemNumber)) ||
                (product.upc && activeDetected.codes.has(product.upc))
              )
            : null;
          const isHighlighted = identityMatches(hoveredIdentity, product.itemNumber, [product.upc]);
          return {
            x1: x,
            y1: y,
            x2: x + width,
            y2: y + height,
            productId: -1,
            itemNumber: product.itemNumber ?? '',
            upc: product.upc ?? '',
            name: product.productName,
            imageUri: product.imageUri,
            modCoverageId: -1,
            shelf: product.shelfNumber ?? undefined,
            column: product.positionNumber ?? undefined,
            embeddingCropCount: product.embeddingCropCount,
            // Default (blue) while the inference is still loading or unavailable.
            color: isHighlighted
              ? HIGHLIGHT_COLOR
              : detected == null
                ? undefined
                : detected
                  ? '#22c55e'
                  : '#ef4444',
            planogramProduct: product,
          };
        }),
    [mod.products, activeDetected, hoveredIdentity]
  );

  return (
    <div id={`pog-reset-mod-${displayNumber}`} className="bg-white rounded-lg shadow-md mb-4">
      <div className="px-4 py-3 border-b flex items-center gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">Mod {displayNumber}</h2>
        <span className="text-sm text-gray-500">
          {mod.planogramItemCount} unique items ({mod.productCount} positions)
        </span>
        {mod.proposed && (
          <span
            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
              mod.proposed.method === 'scan'
                ? 'bg-violet-100 text-violet-800'
                : isWeakProposal
                  ? 'bg-gray-100 text-gray-500'
                  : mod.proposed.assignedPogNumber
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-green-100 text-green-800'
            }`}
            title={
              (mod.proposed.method === 'scan'
                ? `Confirmed by field scan during camera installation (confidence ${formatScore(
                    mod.proposed.score
                  )})` +
                  (mod.proposed.scanAmbiguous
                    ? ' — WARNING: several tags were confirmed for this mod; pick one of the scan options.'
                    : '')
                : `Proposed by ${mod.proposed.method} matcher (score ${formatScore(mod.proposed.score)})`) +
              (isWeakProposal && mod.proposed.method !== 'scan'
                ? ' — below the match threshold, so not auto-accepted; review before accepting.'
                : '') +
              (mod.proposed.assignedPogNumber
                ? ` — WARNING: currently assigned to another active planogram: ${
                    mod.proposed.assignedPogName ?? ''
                  } (${mod.proposed.assignedPogNumber})`
                : '')
            }
          >
            {mod.proposed.assignedPogNumber ? '⚠ ' : ''}
            {mod.proposed.method === 'scan'
              ? mod.proposed.scanAmbiguous
                ? '⚠ Scanned (ambiguous): '
                : 'Scanned: '
              : isWeakProposal
                ? 'Proposed (weak): '
                : 'Proposed: '}
            {mod.proposed.tagName ?? mod.proposed.modCoverageId}
          </span>
        )}
        {mod.proposed?.isTrackingExcluded && (
          <span
            className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 cursor-help"
            title="The proposed mod is excluded from tracking (processing off) — its camera evidence may be stale"
          >
            tracking off
          </span>
        )}
        {mod.proposed?.claimedBy && (
          <ClaimedChip claim={mod.proposed.claimedBy} storeName={storeName} />
        )}
        <PogResetFlagBadges flags={mod.flags} />
        <span className="flex-1" />
        {decision ? (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              decision.source === 'missing'
                ? 'bg-amber-100 text-amber-800'
                : 'bg-blue-100 text-blue-800'
            }`}
          >
            {decision.source === 'missing'
              ? 'Missing in store'
              : `Mapping: ${decision.tagName} (${decision.source})`}
            {!readOnly && (
              <button
                onClick={() => onDecisionChange(null)}
                className={`font-bold ${
                  decision.source === 'missing'
                    ? 'text-amber-600 hover:text-amber-900'
                    : 'text-blue-600 hover:text-blue-900'
                }`}
                title="Clear mapping decision"
              >
                ×
              </button>
            )}
          </span>
        ) : readOnly ? null : (
          <span className="flex items-center gap-2">
            {mod.proposed && (
              <button
                onClick={() => {
                  // pog-reset/start resolves mods by tag name; refuse a tag-less proposal
                  // rather than sending a coverage id in its place.
                  if (!mod.proposed?.tagName) {
                    showErrorToast('This proposal has no shelf tag — pick a tag manually');
                    return;
                  }
                  onDecisionChange({
                    source: 'proposal',
                    tagName: mod.proposed.tagName,
                    modCoverageId: mod.proposed.modCoverageId,
                  });
                }}
                className={`px-2 py-1 text-xs font-medium border rounded-md ${
                  isWeakProposal
                    ? 'text-gray-600 border-gray-400 hover:bg-gray-50'
                    : 'text-green-700 border-green-600 hover:bg-green-50'
                }`}
                title={
                  isWeakProposal
                    ? 'Weak proposal (below match threshold) — accept only if you have verified it'
                    : 'Accept the proposed mapping (saving comes with the review phase)'
                }
              >
                {isWeakProposal ? 'Accept weak proposal' : 'Accept proposal'}
              </button>
            )}
            <span className="flex items-center gap-1">
              <input
                type="text"
                value={manualTag}
                onChange={(e) => setManualTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSetManualTag()}
                placeholder="Manual mod tag"
                className="w-32 px-2 py-1 border border-gray-300 rounded-md text-xs uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleSetManualTag}
                disabled={!manualTag.trim()}
                className="px-2 py-1 text-xs font-medium text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50 disabled:opacity-40"
              >
                Set
              </button>
            </span>
            <button
              onClick={() =>
                onDecisionChange({ source: 'missing', tagName: null, modCoverageId: null })
              }
              className="px-2 py-1 text-xs font-medium text-amber-700 border border-amber-600 rounded-md hover:bg-amber-50"
              title="Mark this mod as physically missing in the store — no POG will be applied for it"
            >
              Missing
            </button>
          </span>
        )}
      </div>

      {(mod.planogramImageUri || selectedCandidate?.imageUri) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 border-b" {...dragGuardProps}>
          <div>
            <div className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
              Camera image
              {selectedCandidate && (
                <span className="font-mono text-xs text-gray-500">
                  {selectedCandidate.tagName ?? selectedCandidate.modCoverageId} (
                  {formatScore(selectedCandidate.score)})
                </span>
              )}
              {selectedCandidate?.inferenceCreatedAt && (
                <StoreDateTime
                  value={selectedCandidate.inferenceCreatedAt}
                  storeTimeZone={storeTimeZone}
                  className="text-xs text-gray-400"
                />
              )}
              <span className="flex-1" />
              <label className="flex items-center gap-1 text-xs text-gray-600 font-normal cursor-pointer">
                <input
                  type="checkbox"
                  checked={showProductPolygons}
                  onChange={(e) => setShowProductPolygons(e.target.checked)}
                />
                <span>Products</span>
              </label>
              <label className="flex items-center gap-1 text-xs text-gray-600 font-normal cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCartonPolygons}
                  onChange={(e) => setShowCartonPolygons(e.target.checked)}
                />
                <span>Cartons</span>
              </label>
            </div>
            {showProductPolygons && (
              <div className="text-xs text-gray-400 mb-1">
                green = product in this mod · blue = in mod but similarity below{' '}
                {Math.round(minCandidateSimilarity * 100)}% (not counted) · red = different product
              </div>
            )}
            {selectedCandidate?.imageUri ? (
              !cameraImage ? (
                // useLoadedImage resets to null on candidate switch, so a stale
                // image is never shown while the new one downloads.
                <div className="h-48 flex items-center justify-center text-sm text-gray-400 border rounded-md animate-pulse">
                  Loading camera image for{' '}
                  {selectedCandidate.tagName ?? selectedCandidate.modCoverageId}…
                </div>
              ) : cameraDetections.length > 0 ? (
                <ImageViewer<BBox>
                  bboxes={[]}
                  image={cameraImage}
                  boxActions={[]}
                  onBoxClick={() => undefined}
                  productRecognitionDetections={cameraDetections}
                  onProductRecognitionClick={(detection) => {
                    if (wasDraggedRef.current) return;
                    setSelectedDetectionItem((detection.item as ProductRecognitionPolygon) ?? null);
                    setSelectedDetectionColor(detection.color);
                  }}
                  onProductRecognitionHover={(detection) =>
                    setHoveredIdentity(
                      detection?.item
                        ? polygonIdentity(detection.item as ProductRecognitionPolygon)
                        : null
                    )
                  }
                  enableZoom
                  fitToContainerWidth
                  maxDisplayHeight={420}
                />
              ) : (
                <img
                  src={cameraImage.src}
                  alt={`Camera for ${selectedCandidate.tagName ?? selectedCandidate.modCoverageId}`}
                  className="w-full max-h-[420px] object-contain border rounded-md cursor-zoom-in bg-gray-50"
                  onClick={() => setZoomImageSrc(selectedCandidate.imageUri)}
                />
              )
            ) : (
              <div className="h-48 flex items-center justify-center text-sm text-gray-400 border rounded-md">
                No camera image
              </div>
            )}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-600 mb-2">
              Planogram — click a product for details
              {activeDetected && (
                <span className="text-xs text-gray-400 font-normal">
                  {' '}
                  (green = detected in the enabled camera layers, red = not detected)
                </span>
              )}
            </div>
            {mod.planogramImageUri ? (
              planogramImage ? (
                <ImageViewer<BBox>
                  bboxes={productBoxes}
                  image={planogramImage}
                  boxActions={[]}
                  onBoxClick={(bbox) => {
                    if (wasDraggedRef.current) return;
                    setSelectedProductIndex(
                      mod.products.indexOf(
                        (bbox as BBox & { planogramProduct: ControlPogResetPlanogramProduct })
                          .planogramProduct
                      )
                    );
                  }}
                  onBoxHover={(bbox) =>
                    setHoveredIdentity(
                      bbox
                        ? {
                            items: new Set(bbox.itemNumber ? [bbox.itemNumber] : []),
                            codes: new Set(bbox.upc ? [bbox.upc] : []),
                          }
                        : null
                    )
                  }
                  showEmbeddingsCount={showEmbeddingsCount}
                  enableZoom
                  fitToContainerWidth
                  maxDisplayHeight={420}
                />
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-gray-400 border rounded-md">
                  Loading planogram image…
                </div>
              )
            ) : (
              <div className="h-48 flex items-center justify-center text-sm text-gray-400 border rounded-md">
                No planogram image for this mod.
              </div>
            )}
          </div>
        </div>
      )}

      {mod.candidates.length > 0 || scanAsCandidates.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-gray-600">
              <th className="px-4 py-2 font-medium w-16">Rank</th>
              <th className="px-4 py-2 font-medium">Mod Tag</th>
              <th className="px-4 py-2 font-medium" title="F1 of coverage and precision">
                Score
              </th>
              <th
                className="px-4 py-2 font-medium"
                title="Coverage: planogram items found on the shelf"
              >
                Coverage
              </th>
              <th
                className="px-4 py-2 font-medium"
                title="Precision: shelf items belonging to the planogram"
              >
                Precision
              </th>
              <th
                className="px-4 py-2 font-medium"
                title="TEMP: latest mod-compliance obstruction ratio for this candidate's image"
              >
                Obstruction
              </th>
              <th className="px-4 py-2 font-medium">Camera</th>
              <th className="px-4 py-2 font-medium">Control Image</th>
              <th className="px-4 py-2 font-medium">Remote DC</th>
              <th className="px-4 py-2 font-medium w-16" />
            </tr>
          </thead>
          <tbody>
            {[...scanAsCandidates, ...mod.candidates].map((candidate) => {
              // Scan rows (rank 0) are install-scan options shown alongside PR candidates;
              // their score is the scan confidence and the PR-only columns stay empty.
              const isScanRow = candidate.rank === 0;
              const isProposed = mod.proposed?.modCoverageId === candidate.modCoverageId;
              const isSelected = selectedCandidate?.modCoverageId === candidate.modCoverageId;
              return (
                <tr
                  key={candidate.modCoverageId}
                  onClick={() => setSelectedCandidateId(candidate.modCoverageId)}
                  className={`border-b last:border-b-0 cursor-pointer ${
                    isSelected ? 'bg-blue-50' : isProposed ? 'bg-green-50' : 'hover:bg-gray-50'
                  }`}
                  title="Click to preview this candidate's camera image"
                >
                  <td className="px-4 py-2">
                    {isScanRow ? (
                      <span className="text-xs font-medium text-violet-700">scan</span>
                    ) : (
                      `#${candidate.rank}`
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono">
                    {candidate.tagName ?? '—'}
                    {isProposed && (
                      <span
                        className={`ml-2 text-xs font-sans font-medium ${
                          mod.proposed?.method === 'scan' ? 'text-violet-700' : 'text-green-700'
                        }`}
                      >
                        {mod.proposed?.method === 'scan' ? 'scanned' : 'proposed'}
                      </span>
                    )}
                    {isScanRow && !isProposed && (
                      <span
                        className="ml-2 text-xs font-sans font-medium text-violet-700 cursor-help"
                        title="Alternative tag confirmed by field scan during camera installation"
                      >
                        scan option
                      </span>
                    )}
                    {candidate.assignedPogNumber && (
                      <span
                        className="ml-2 text-xs font-sans font-medium text-amber-600 cursor-help"
                        title={`Currently assigned to another active planogram: ${
                          candidate.assignedPogName ?? ''
                        } (${candidate.assignedPogNumber})`}
                      >
                        ⚠ occupied
                      </span>
                    )}
                    {candidate.isTrackingExcluded && (
                      <span
                        className="ml-2 px-2 py-0.5 rounded-full text-xs font-sans font-medium bg-amber-100 text-amber-800 cursor-help"
                        title="This mod is excluded from tracking (processing off) — its camera evidence may be stale"
                      >
                        tracking off
                      </span>
                    )}
                    {candidate.claimedBy && (
                      <ClaimedChip claim={candidate.claimedBy} storeName={storeName} />
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            isScanRow
                              ? 'bg-violet-500'
                              : candidate.score >= matchThreshold
                                ? 'bg-green-500'
                                : 'bg-red-400'
                          }`}
                          style={{ width: `${Math.min(100, Math.round(candidate.score * 100))}%` }}
                        />
                      </div>
                      <span className="font-medium">{formatScore(candidate.score)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    {candidate.coverage != null ? formatScore(candidate.coverage) : '—'}
                    {candidate.coverage != null && (
                      <span className="text-xs text-gray-500 ml-1">
                        ({candidate.matchedCount}/{mod.planogramItemCount})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {candidate.precision != null ? formatScore(candidate.precision) : '—'}
                    {candidate.realogramItemCount != null && (
                      <span className="text-xs text-gray-500 ml-1">
                        ({candidate.matchedCount}/{candidate.realogramItemCount})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {candidate.obstructionRatio != null
                      ? formatScore(candidate.obstructionRatio)
                      : '—'}
                  </td>
                  <td className="px-4 py-2">{candidate.cameraId ?? '—'}</td>
                  <td className="px-4 py-2">
                    {candidate.deviceName && candidate.imageId != null ? (
                      <Link
                        to={`/stores/${storeName}/${candidate.deviceName}?image_id=${candidate.imageId}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        Open <OpenInNewIcon fontSize="inherit" />
                      </Link>
                    ) : candidate.imageUri ? (
                      <a
                        href={candidate.imageUri}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        Open <OpenInNewIcon fontSize="inherit" />
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {candidate.imageId != null ? (
                      <a
                        href={`${FORGE_REMOTE_DC_TOOL_PATH}?image_id=${candidate.imageId}&load_pr=1`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        Open <OpenInNewIcon fontSize="inherit" />
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // pog-reset/start resolves mods by tag name; refuse a tag-less
                        // candidate rather than sending a coverage id in its place.
                        if (!candidate.tagName) {
                          showErrorToast('This candidate has no shelf tag — pick a tag manually');
                          return;
                        }
                        onDecisionChange({
                          source: isScanRow ? 'scan' : 'candidate',
                          tagName: candidate.tagName,
                          modCoverageId: candidate.modCoverageId,
                        });
                      }}
                      className="px-2 py-1 text-xs font-medium text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50"
                      title="Use this candidate as the mapping (saving comes with the review phase)"
                    >
                      Use
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="px-4 py-6 text-center text-gray-500 text-sm">
          No candidate mods matched any product of this planogram mod.
        </div>
      )}

      <PlanogramProductDialog
        product={selectedProductIndex != null ? (mod.products[selectedProductIndex] ?? null) : null}
        onClose={() => setSelectedProductIndex(null)}
        onPrev={
          selectedProductIndex != null && selectedProductIndex > 0
            ? () => setSelectedProductIndex(selectedProductIndex - 1)
            : undefined
        }
        onNext={
          selectedProductIndex != null && selectedProductIndex < mod.products.length - 1
            ? () => setSelectedProductIndex(selectedProductIndex + 1)
            : undefined
        }
        positionLabel={
          selectedProductIndex != null
            ? `${selectedProductIndex + 1} of ${mod.products.length}`
            : undefined
        }
      />
      {selectedDetectionItem && (
        <ImageItemDetailsDialog
          item={selectedDetectionItem}
          cameraImageElement={cameraImage ?? undefined}
          isOpen={selectedDetectionItem !== null}
          onClose={() => setSelectedDetectionItem(null)}
          layerBboxColor={selectedDetectionColor}
          cartonBoxes={
            selectedCandidate?.imageId != null && prByImageId[selectedCandidate.imageId] !== 'error'
              ? (prByImageId[selectedCandidate.imageId] as ProductRecognitionResponse | undefined)
                  ?.cartonBoxes
              : undefined
          }
          inferenceMetadata={
            selectedCandidate?.imageId != null && prByImageId[selectedCandidate.imageId] !== 'error'
              ? (prByImageId[selectedCandidate.imageId] as ProductRecognitionResponse | undefined)
                  ?.inferenceMetadata
              : undefined
          }
        />
      )}
      {zoomImageSrc && (
        <ImageZoomModal
          isOpen={zoomImageSrc !== null}
          imageSrc={zoomImageSrc}
          onClose={() => setZoomImageSrc(null)}
        />
      )}
    </div>
  );
};
